import $ from 'cash-dom'
import { cart } from './cart'
import {
  resetSignInForm,
  resendEmailCode,
  callMultipass,
} from '../auth/sign-in.ts'
import { Auth } from 'aws-amplify'
import { lockScroll, unlockScroll } from '../utilities/scroll-lock'

class AccountPanel {
  constructor() {
    this.panel = $('#account-panel')
    this.signUpPanel = $('#account-sign-up-panel')
    this.resetPasswordPanel = $('#account-reset-password-panel')
    this.verifyPanel = $('#account-verify-panel')
    this.emailVerifyPanel = $('#account-email-verify-panel')
    this.completeSignupPanel = $('#account-complete-signup-panel')
    this.completeNewAccountPanel = $('#account-complete-new-account-panel')
    this.sessionExpiredPanel = $('#account-session-expired-panel')
    this.unrecognizedEmailPanel = $('#account-unrecognized-email-panel')
    this.incompleteSocialPanel = $('#account-incomplete-social-panel')
    this.passwordPanel = $('#account-password-panel')
    this.open = false

    this.bindUI()
  }

  // Helper method to clear form fields and reset validation states
  clearFormFields(panel, fieldSelectors = []) {
    fieldSelectors.forEach((selector) => {
      const field = panel.find(selector)
      if (field.attr('type') === 'checkbox') {
        field.prop('checked', false)
      } else {
        field.val('')
      }
    })
  }

  // Helper method to clear text content
  clearTextContent(panel, selectors = []) {
    selectors.forEach((selector) => {
      panel.find(selector).text('')
    })
  }

  // Helper method to reset validation states
  resetValidationStates(panel) {
    panel.find('.input-group').each(function () {
      $(this).attr('data-valid', '')
      $(this).removeClass('invalid')
      $(this).find('.input-group-error').addClass('hidden').html('')
    })
  }

  // Helper method to clear error messages
  clearErrorMessages(panel) {
    const errorMessage = panel.find('.error-message')
    errorMessage.addClass('hidden').text('')
  }

  // Helper method to reset email verification button state
  resetEmailVerifyButton() {
    const verifyButton = this.emailVerifyPanel.find('[type="submit"]')
    verifyButton.text('Verify Email')
    verifyButton.removeClass('button-success')
    verifyButton.attr('disabled', true)
  }

  // Helper method to reset email verification code field
  resetEmailVerificationCode() {
    this.emailVerifyPanel.find('#email-verification-code').val('')
    const codeInputGroup = this.emailVerifyPanel
      .find('#email-verification-code')
      .closest('.input-group')
    codeInputGroup.attr('data-valid', '')
    codeInputGroup.removeClass('invalid')
    codeInputGroup.find('.input-group-error').addClass('hidden').html('')
  }

  // Comprehensive method to clear complete new account panel
  clearCompleteNewAccountPanel() {
    this.clearFormFields(this.completeNewAccountPanel, [
      '#complete-new-account-first-name',
      '#complete-new-account-last-name',
      '#complete-new-account-password',
      '#complete-new-account-confirm-password',
      '#complete-new-account-terms',
      '#complete-new-account-email',
      '#complete-new-account-verification-code',
      '#complete-new-account-session-id',
    ])

    this.clearTextContent(this.completeNewAccountPanel, [
      '#complete-new-account-email-display',
    ])

    this.resetValidationStates(this.completeNewAccountPanel)
    this.clearErrorMessages(this.completeNewAccountPanel)
  }

  // Comprehensive method to clear complete signup panel
  clearCompleteSignupPanel() {
    this.clearFormFields(this.completeSignupPanel, [
      '#complete-signup-first-name',
      '#complete-signup-last-name',
      '#complete-signup-password',
      '#complete-signup-confirm-password',
      '#complete-signup-terms',
      '#complete-signup-email',
      '#complete-signup-puid',
      '#complete-signup-verification-code',
      '#complete-signup-session-id',
    ])

    this.clearTextContent(this.completeSignupPanel, [
      '#complete-signup-email-display',
    ])

    this.resetValidationStates(this.completeSignupPanel)
    this.clearErrorMessages(this.completeSignupPanel)
  }

  // Comprehensive method to clear email verification panel
  clearEmailVerificationPanel() {
    this.clearFormFields(this.emailVerifyPanel, [
      '#email-verification-code',
      '#email-verification-email',
      '#email-verification-session-id',
    ])

    this.clearTextContent(this.emailVerifyPanel, [
      '#email-verification-display',
    ])

    this.resetEmailVerificationCode()
    this.resetEmailVerifyButton()
    this.clearErrorMessages(this.emailVerifyPanel)
  }

  // Comprehensive method to clear main sign-in panel
  clearMainSignInPanel() {
    this.clearFormFields(this.panel, ['#signin-email'])
    this.resetValidationStates(this.panel)
    this.clearErrorMessages(this.panel)
    // Reset the sign-in form button state
    resetSignInForm()
  }

  // Comprehensive method to clear password panel
  clearPasswordPanel() {
    this.clearFormFields(this.passwordPanel, ['#password-signin-password'])
    this.resetValidationStates(this.passwordPanel)
    this.clearErrorMessages(this.passwordPanel)
    this.passwordPanel
      .find('#password-signin-password')
      .closest('.input-group')
      .removeAttr('data-auth-error')
  }

  clearAllPanels() {
    this.clearMainSignInPanel()
    this.clearPasswordPanel()
    this.clearEmailVerificationPanel()
    this.clearCompleteNewAccountPanel()
    this.clearCompleteSignupPanel()
  }

  bindUI() {
    $('[data-action="goto-cart-panel"]').on('click', (e) => {
      e.preventDefault()
      this.hide()
      cart.show()
    })

    $('[data-action="show-account-panel"]').on('click', (e) => {
      e.preventDefault()
      e.target.setAttribute('aria-expanded', 'true')
      this.show()
    })
    $('[data-action="hide-account-panel"]').on('click', (e) => {
      if ($(e.target).closest('#account-unrecognized-email-panel').length > 0) {
        return
      }
      // Prevent clicking away from the social incomplete panel
      if (this.incompleteSocialPanel.attr('aria-hidden') === 'false') {
        return
      }
      e.preventDefault()
      this.hide()
    })

    $('[data-action="show-create-account-subpanel"]').on('click', (e) => {
      e.preventDefault()

      this.signUpPanel.attr('aria-hidden', 'false')
      this.show()
    })

    $('[data-action="hide-create-account-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.signUpPanel.attr('aria-hidden', 'true')
      this.checkFieldValidation()
    })

    $('[data-action="show-reset-password-subpanel"]').on('click', (e) => {
      e.preventDefault()
      const signInEmailEl = document.getElementById('signin-email')
      const passwordEmailEl = document.getElementById('password-signin-email')
      const emailValue =
        (signInEmailEl && signInEmailEl.value) ||
        (passwordEmailEl && passwordEmailEl.value) ||
        ''

      const sendResetEmailInput = document.getElementById('send-reset-email')
      if (emailValue && sendResetEmailInput) {
        sendResetEmailInput.value = emailValue
        var changeEvent = new Event('change', { bubbles: true })
        sendResetEmailInput.dispatchEvent(changeEvent)
      }

      this.passwordPanel.attr('aria-hidden', 'true')
      this.resetPasswordPanel.attr('aria-hidden', 'false')
      this.checkFieldValidation()
    })

    $('[data-action="hide-reset-password-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.resetPasswordPanel.attr('aria-hidden', 'true')

      resetSignInForm()
      this.checkFieldValidation()
    })

    $('[data-action="hide-password-subpanel"]').on('click', (e) => {
      e.preventDefault()

      // Use helper method to clear password panel
      this.clearPasswordPanel()

      // Also reset the main sign-in form state when navigating back
      resetSignInForm()

      this.passwordPanel.attr('aria-hidden', 'true')
      this.checkFieldValidation()
    })

    $('[data-action="show-verify-subpanel"]').on('click', (e) => {
      e.preventDefault()
    })

    $('[data-action="hide-verify-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.verifyPanel.attr('aria-hidden', 'true')
    })

    $('[data-action="hide-email-verify-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.emailVerifyPanel.attr('aria-hidden', 'true')

      if ($(e.target).text().trim() === 'Change email') {
        this.panel.find('#signin-email').val('')
      }

      this.emailVerifyPanel.find('#email-verification-code').val('')
      resetSignInForm()
    })

    $('[data-action="hide-complete-signup-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.completeSignupPanel.attr('aria-hidden', 'true')

      const verifyButton = this.emailVerifyPanel.find('[type="submit"]')
      verifyButton.text('Verify Email')
      verifyButton.removeClass('button-success')
      verifyButton.removeAttr('disabled')

      this.resetEmailVerificationCode()

      this.emailVerifyPanel.attr('aria-hidden', 'false')
      this.checkFieldValidation()
    })

    $('[data-action="hide-complete-new-account-subpanel"]').on('click', (e) => {
      e.preventDefault()
      // Pull values from the complete new account panel
      const email = this.completeNewAccountPanel
        .find('#complete-new-account-email')
        .val()
      const code = this.completeNewAccountPanel
        .find('#complete-new-account-verification-code')
        .val()
      const sessionId = this.completeNewAccountPanel
        .find('#complete-new-account-session-id')
        .val()

      // Hide the new account panel
      this.completeNewAccountPanel.attr('aria-hidden', 'true')

      // Restore values and show the unrecognized email panel
      if (email)
        this.unrecognizedEmailPanel.find('#unrecognized-email').val(email)
      if (code)
        this.unrecognizedEmailPanel.find('#unrecognized-email-code').val(code)
      if (sessionId)
        this.unrecognizedEmailPanel
          .find('#unrecognized-email-session-id')
          .val(sessionId)

      this.unrecognizedEmailPanel.attr('aria-hidden', 'true')
      this.unrecognizedEmailPanel.attr('aria-hidden', 'false')
      this.show()
      this.checkFieldValidation()
    })

    $('[data-action="clear-and-hide-complete-new-account-subpanel"]').on(
      'click',
      (e) => {
        e.preventDefault()

        // Use helper method to clear all fields and reset states
        this.clearCompleteNewAccountPanel()

        // Hide the new account panel
        this.completeNewAccountPanel.attr('aria-hidden', 'true')

        // Show the unrecognized email panel (without restoring values since we're clearing)
        this.unrecognizedEmailPanel.attr('aria-hidden', 'false')
        this.show()
        this.checkFieldValidation()
      }
    )

    $('[data-action="clear-and-show-signin-panel"]').on('click', (e) => {
      e.preventDefault()

      // Use helper methods to clear all fields and reset states
      this.clearCompleteNewAccountPanel()
      this.clearEmailVerificationPanel()

      // Reset the sign-in form
      resetSignInForm()

      // Hide the new account panel
      this.completeNewAccountPanel.attr('aria-hidden', 'true')

      // Hide email verification panel if it's visible
      this.emailVerifyPanel.attr('aria-hidden', 'true')

      // Show the main account panel (sign-in panel)
      this.show()
      this.checkFieldValidation()
    })

    $('[data-action="hide-session-expired-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.sessionExpiredPanel.attr('aria-hidden', 'true')
    })

    $('[data-action="send-new-verification-code"]').on('click', (e) => {
      e.preventDefault()
      const email = this.sessionExpiredPanel
        .find('#session-expired-email')
        .val()
      if (email) {
        this.sessionExpiredPanel.attr('aria-hidden', 'true')
        this.resetEmailVerificationCode()

        this.emailVerifyPanel.attr('aria-hidden', 'false')
        this.checkFieldValidation()

        resendEmailCode(email)
      }
    })

    $(document).on(
      'click',
      '[data-action="resend-email-verification-code"]',
      (e) => {
        e.preventDefault()
        const email = this.emailVerifyPanel
          .find('#email-verification-email')
          .val()

        if (email) {
          // Clear the verification code field and reset validation state
          this.resetEmailVerificationCode()

          // Disable the verify email button
          const verifyButton = this.emailVerifyPanel.find('[type="submit"]')
          verifyButton.attr('disabled', true)

          resendEmailCode(email)
        }
      }
    )

    // Handle back button on unrecognized email panel - show confirmation modal
    $('[data-action="hide-unrecognized-email-subpanel"]').on('click', (e) => {
      e.preventDefault()

      // Show the same confirmation modal as the cancel button
      const modal = this.unrecognizedEmailPanel.find(
        '#cancel-confirmation-modal'
      )
      modal.removeClass('hidden')
    })

    $('[data-action="try-another-email"]').on('click', (e) => {
      e.preventDefault()
      // Hide unrecognized email panel and go back to sign-in
      this.unrecognizedEmailPanel.attr('aria-hidden', 'true')

      // Use helper methods to clear all relevant panels
      this.clearMainSignInPanel()
      this.clearEmailVerificationPanel()

      // Hide the email verify panel if it's visible
      this.emailVerifyPanel.attr('aria-hidden', 'true')

      // Focus on the email field
      this.checkFieldValidation()
    })

    $('[data-action="show-create-new-account-panel"]').on('click', (e) => {
      e.preventDefault()
      const email = this.unrecognizedEmailPanel
        .find('#unrecognized-email')
        .val()
      const code = this.unrecognizedEmailPanel
        .find('#unrecognized-email-code')
        .val()
      const sessionId = this.unrecognizedEmailPanel
        .find('#unrecognized-email-session-id')
        .val()

      // Hide unrecognized email panel
      this.unrecognizedEmailPanel.attr('aria-hidden', 'true')

      // Show create new account panel with the email and verification data
      if (email && code && sessionId) {
        this.showCreateNewAccountPanel(email, code, sessionId)
      }
    })

    // Handle cancel button on unrecognized email panel - show confirmation modal
    this.unrecognizedEmailPanel
      .find('[data-action="hide-account-panel"]')
      .on('click', (e) => {
        e.preventDefault()
        e.stopPropagation() // Prevent the general hide-account-panel handler from firing

        // Show the confirmation modal
        const modal = this.unrecognizedEmailPanel.find(
          '#cancel-confirmation-modal'
        )
        modal.removeClass('hidden')
      })

    // Handle closing the confirmation modal (just close the modal, don't close the panel)
    $(document).on('click', '[data-action="close-confirmation-modal"]', (e) => {
      e.preventDefault()
      const modal = $('#cancel-confirmation-modal')
      modal.addClass('hidden')
      // Panel stays open - user just dismissed the modal
    })

    // Handle confirming the cancel action (go back to beginning with cleared fields)
    $(document).on('click', '[data-action="confirm-cancel"]', (e) => {
      e.preventDefault()
      const modal = $('#cancel-confirmation-modal')
      modal.addClass('hidden')

      // Use comprehensive helper method to clear all panels
      this.clearAllPanels()

      // Hide all sub-panels to go back to the main sign-in form
      this.signUpPanel.attr('aria-hidden', 'true')
      this.resetPasswordPanel.attr('aria-hidden', 'true')
      this.verifyPanel.attr('aria-hidden', 'true')
      this.emailVerifyPanel.attr('aria-hidden', 'true')
      this.completeSignupPanel.attr('aria-hidden', 'true')
      this.sessionExpiredPanel.attr('aria-hidden', 'true')
      this.unrecognizedEmailPanel.attr('aria-hidden', 'true')
      this.passwordPanel.attr('aria-hidden', 'true')

      // Keep the main panel open and focus on the email field
      this.checkFieldValidation()
    })

    // Handle complete signup confirmation modal
    $('[data-action="show-complete-signup-confirmation-modal"]').on(
      'click',
      (e) => {
        e.preventDefault()
        const modal = $('#complete-signup-confirmation-modal')
        modal.removeClass('hidden')
      }
    )

    // Handle closing the complete signup confirmation modal
    $(document).on(
      'click',
      '[data-action="close-complete-signup-confirmation-modal"]',
      (e) => {
        e.preventDefault()
        const modal = $('#complete-signup-confirmation-modal')
        modal.addClass('hidden')
      }
    )

    // Handle confirming the complete signup cancel action
    $(document).on(
      'click',
      '[data-action="confirm-complete-signup-cancel"]',
      (e) => {
        e.preventDefault()
        const modal = $('#complete-signup-confirmation-modal')
        modal.addClass('hidden')

        // Use helper methods to clear all relevant panels
        this.clearMainSignInPanel()
        this.clearEmailVerificationPanel()

        // Hide all sub-panels to go back to the main sign-in form
        this.signUpPanel.attr('aria-hidden', 'true')
        this.resetPasswordPanel.attr('aria-hidden', 'true')
        this.verifyPanel.attr('aria-hidden', 'true')
        this.emailVerifyPanel.attr('aria-hidden', 'true')
        this.completeSignupPanel.attr('aria-hidden', 'true')
        this.sessionExpiredPanel.attr('aria-hidden', 'true')
        this.unrecognizedEmailPanel.attr('aria-hidden', 'true')
        this.incompleteSocialPanel.attr('aria-hidden', 'true')
        this.passwordPanel.attr('aria-hidden', 'true')

        // Keep the main panel open and focus on the email field
        this.checkFieldValidation()
      }
    )

    // Handle email unverified panel events
    $('[data-action="hide-email-unverified-subpanel"]').on('click', (e) => {
      e.preventDefault()
      this.incompleteSocialPanel.attr('aria-hidden', 'true')
      this.clearMainSignInPanel()
      this.checkFieldValidation()
    })

    // Handle social incomplete confirmation modal
    $('[data-action="show-social-incomplete-confirmation-modal"]').on(
      'click',
      (e) => {
        e.preventDefault()
        const modal = $('#social-incomplete-confirmation-modal')
        modal.removeClass('hidden')
      }
    )

    // Handle closing the social incomplete confirmation modal
    $(document).on(
      'click',
      '[data-action="close-social-incomplete-confirmation-modal"]',
      (e) => {
        e.preventDefault()
        const modal = $('#social-incomplete-confirmation-modal')
        modal.addClass('hidden')
      }
    )

    // Handle confirming the social incomplete cancel action
    $(document).on(
      'click',
      '[data-action="confirm-social-incomplete-cancel"]',
      (e) => {
        e.preventDefault()
        const modal = $('#social-incomplete-confirmation-modal')
        modal.addClass('hidden')

        // Hide the social incomplete panel and go back to sign-in
        this.incompleteSocialPanel.attr('aria-hidden', 'true')
        this.clearMainSignInPanel()
        this.checkFieldValidation()
      }
    )

    // Handle create account from social form submission
    $(document).on('submit', '#create-account-from-social-form', (e) => {
      e.preventDefault()

      const form = $(e.target)
      const email = form.find('#create-social-email').val()
      const firstName = form.find('#create-social-first-name').val().trim()
      const lastName = form.find('#create-social-last-name').val().trim()
      const agreeToTerms = form.find('#create-social-terms').is(':checked')

      if (!firstName || !lastName) {
        this.showSocialAccountMessage(
          'Please fill in all required fields',
          true
        )
        return
      }

      if (!agreeToTerms) {
        this.showSocialAccountMessage(
          'Please agree to the Terms & Conditions',
          true
        )
        return
      }

      this.createAccountFromSocial(email, firstName, lastName)
    })

    this.panel.find('nav a').on('click', (e) => {
      this.hide()
    })

    this.checkHash()
  }

  show() {
    lockScroll()
    this.panel.attr('aria-hidden', 'false')
    this.open = true
    this.checkFieldValidation()
  }

  hide() {
    unlockScroll()
    // Use comprehensive helper method to clear all panels and their error states
    this.clearAllPanels()

    this.panel.attr('aria-hidden', 'true')
    this.signUpPanel.attr('aria-hidden', 'true')
    this.resetPasswordPanel.attr('aria-hidden', 'true')
    this.verifyPanel.attr('aria-hidden', 'true')
    this.emailVerifyPanel.attr('aria-hidden', 'true')
    this.completeSignupPanel.attr('aria-hidden', 'true')
    this.completeNewAccountPanel.attr('aria-hidden', 'true')
    this.sessionExpiredPanel.attr('aria-hidden', 'true')
    this.unrecognizedEmailPanel.attr('aria-hidden', 'true')
    this.incompleteSocialPanel.attr('aria-hidden', 'true')
    this.passwordPanel.attr('aria-hidden', 'true')
    this.open = false

    $('[aria-controls="account-panel"][aria-expanded="true"]').focus()
    $('[aria-controls="account-panel"]').attr('aria-expanded', 'false')
  }

  checkFieldValidation() {
    const panels = [
      {
        panel: this.completeSignupPanel,
        selector: '#complete-signup-first-name',
        triggerChange: false,
      },
      {
        panel: this.completeNewAccountPanel,
        selector: '#complete-new-account-first-name',
        triggerChange: false,
      },
      {
        panel: this.emailVerifyPanel,
        selector: '#email-verification-code',
        triggerChange: false,
      },
      {
        panel: this.verifyPanel,
        selector: '#verification-code',
        triggerChange: false,
      },
      {
        panel: this.resetPasswordPanel,
        selector: '#send-reset-email',
        triggerChange: false,
      },
      {
        panel: this.signUpPanel,
        selector: '#sign-up-first-name',
        triggerChange: false,
      },
      {
        panel: this.panel,
        selector: '#signin-email',
        triggerChange: true,
      },
      {
        panel: this.passwordPanel,
        selector: '#password-signin-password',
        triggerChange: false,
      },
    ]

    const topPanel = panels.find(({ panel, selector }) => {
      if (panel?.attr('aria-hidden') !== 'false') return false
      return panel.find(selector).length
    })

    if (!topPanel) return

    const field = topPanel.panel.find(topPanel.selector)
    setTimeout(() => {
      field.focus()
      if (topPanel.triggerChange) field.trigger('change')
    }, 200)
  }

  checkHash() {
    if (window.location.hash == '#show-create-account-subpanel') {
      setTimeout(() => {
        this.show()
      }, 1000)
    }
  }

  showVerifyPanel(email, password) {
    this.verifyPanel.find('#verification-email').val(email)
    this.verifyPanel.find('#verification-password').val(password)

    this.verifyPanel.attr('aria-hidden', 'false')
    this.checkFieldValidation()
  }

  showEmailVerifyPanel(email, sessionId) {
    this.emailVerifyPanel.find('#email-verification-email').val(email)
    this.emailVerifyPanel.find('#email-verification-session-id').val(sessionId)
    this.emailVerifyPanel.find('#email-verification-display').text(email)

    this.emailVerifyPanel.attr('aria-hidden', 'false')
    this.show()
    this.checkFieldValidation()
  }

  showCreateNewAccountPanel(email, verificationCode, sessionId) {
    this.completeNewAccountPanel.find('#complete-new-account-email').val(email)

    const emailDisplay = this.completeNewAccountPanel.find(
      '#complete-new-account-email-display'
    )
    emailDisplay.text(email)

    if (email.length > 28) {
      emailDisplay.addClass('text-body-sm')
    } else {
      emailDisplay.removeClass('text-body-sm')
    }

    this.completeNewAccountPanel
      .find('#complete-new-account-verification-code')
      .val(verificationCode)
    this.completeNewAccountPanel
      .find('#complete-new-account-session-id')
      .val(sessionId)

    this.completeNewAccountPanel.attr('aria-hidden', 'false')
    this.show()
    this.checkFieldValidation()

    setTimeout(() => {
      this.completeSignupPanel
        .find('#account-complete-signup-form')
        .trigger('validate-form')
    }, 100)
  }

  showCompleteSignupPanel(
    email,
    puid,
    verificationCode,
    sessionId,
    firstName = '',
    lastName = ''
  ) {
    this.completeSignupPanel.find('#complete-signup-email').val(email)
    this.completeSignupPanel.find('#complete-signup-puid').val(puid)
    this.completeSignupPanel
      .find('#complete-signup-verification-code')
      .val(verificationCode)
    this.completeSignupPanel.find('#complete-signup-session-id').val(sessionId)
    this.completeSignupPanel.find('#complete-signup-email-display').text(email)

    if (firstName) {
      const firstNameField = this.completeSignupPanel.find(
        '#complete-signup-first-name'
      )
      firstNameField.val(firstName)
      if (firstName.trim().length > 0) {
        firstNameField.closest('.input-group').attr('data-valid', 'true')
      }
    }
    if (lastName) {
      const lastNameField = this.completeSignupPanel.find(
        '#complete-signup-last-name'
      )
      lastNameField.val(lastName)
      if (lastName.trim().length > 0) {
        lastNameField.closest('.input-group').attr('data-valid', 'true')
      }
    }
    this.emailVerifyPanel.attr('aria-hidden', 'true')

    this.completeSignupPanel.attr('aria-hidden', 'false')
    this.show()
    this.checkFieldValidation()

    setTimeout(() => {
      this.completeSignupPanel
        .find('#account-complete-signup-form')
        .trigger('validate-form')
    }, 100)
  }

  showSessionExpiredPanel(email) {
    this.sessionExpiredPanel.find('#session-expired-email').val(email)

    // Hide the email verification panel first
    this.emailVerifyPanel.attr('aria-hidden', 'true')

    this.sessionExpiredPanel.attr('aria-hidden', 'false')
    this.show()
  }

  showUnrecognizedEmailPanel(email, code, sessionId) {
    this.unrecognizedEmailPanel.find('#unrecognized-email').val(email)
    this.unrecognizedEmailPanel.find('#unrecognized-email-code').val(code)
    this.unrecognizedEmailPanel
      .find('#unrecognized-email-session-id')
      .val(sessionId)

    // Hide the email verification panel first
    this.emailVerifyPanel.attr('aria-hidden', 'true')

    this.unrecognizedEmailPanel.attr('aria-hidden', 'false')
    this.show()
  }

  showPasswordPanel(email) {
    this.passwordPanel.find('#password-signin-email').val(email)
    this.passwordPanel.find('#password-signin-display').text(email)
    this.passwordPanel.attr('aria-hidden', 'false')
    this.show()
    this.checkFieldValidation()
  }

  showSocialIncompletePanel(
    email,
    returnUrl = '/',
    provider = 'social',
    firstName = '',
    lastName = ''
  ) {
    this.incompleteSocialPanel.find('#create-social-email').val(email)
    const providerDisplayName = this.getProviderDisplayName(provider)
    this.incompleteSocialPanel.find('#account-type').text(providerDisplayName)

    // Pre-populate name fields if available
    if (firstName) {
      const firstNameField = this.incompleteSocialPanel.find(
        '#create-social-first-name'
      )
      firstNameField.val(firstName)
      if (firstName.trim().length > 0) {
        firstNameField.closest('.input-group').attr('data-valid', 'true')
      }
    }
    if (lastName) {
      const lastNameField = this.incompleteSocialPanel.find(
        '#create-social-last-name'
      )
      lastNameField.val(lastName)
      if (lastName.trim().length > 0) {
        lastNameField.closest('.input-group').attr('data-valid', 'true')
      }
    }

    // Store return URL for later use
    this.incompleteSocialPanel.attr('data-return-url', returnUrl)

    this.incompleteSocialPanel.attr('aria-hidden', 'false')
    this.show()
    this.checkFieldValidation()

    // Trigger form validation after pre-populating fields to enable the submit button
    setTimeout(() => {
      this.incompleteSocialPanel
        .find('#create-account-from-social-form')
        .trigger('validate-form')
    }, 100)
  }

  getProviderDisplayName(provider) {
    const providerNames = {
      'google': 'Gmail',
      'facebook': 'Facebook',
      'apple': 'Apple',
      'social': 'Social Login',
    }
    return providerNames[provider] || 'Social Login'
  }

  showSocialAccountMessage(message, isError = false) {
    const messageElement = this.incompleteSocialPanel.find(
      '[data-content="create-social-response-message"]'
    )
    messageElement.text(message)
    messageElement.removeClass('hidden')

    if (isError) {
      messageElement.addClass('text-red-600')
    } else {
      messageElement.removeClass('text-red-600')
    }

    setTimeout(() => {
      messageElement.addClass('hidden')
      messageElement.text('')
      messageElement.removeClass('text-red-600')
    }, 3000)
  }

  async createAccountFromSocial(email, firstName, lastName) {
    const subsystemURL = !window.Pura?.environment?.production
      ? 'https://user.stg.trypura.io'
      : 'https://user.trypura.io'

    const currentUser = await Auth.currentAuthenticatedUser()
    const accessToken =
      currentUser?.signInUserSession?.accessToken?.jwtToken || {}
    const idToken = currentUser?.signInUserSession?.idToken?.jwtToken || {}
    const cognitoUsername = currentUser?.username

    try {
      const response = await fetch(
        `${subsystemURL}/v1/users/create-from-social`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken,
          },
          body: JSON.stringify({
            email: email,
            cognitoUsername: cognitoUsername,
            firstName: firstName,
            lastName: lastName,
          }),
        }
      )

      const responseData = await response.json()

      if (response.ok && responseData) {
        const returnUrl =
          this.incompleteSocialPanel.attr('data-return-url') || '/'

        try {
          const multipassResult = await callMultipass(idToken, returnUrl)
          setTimeout(() => {
            this.incompleteSocialPanel.attr('aria-hidden', 'true')
            window.location.href = multipassResult.signin_url
          }, 1500)
        } catch (multipassError) {
          console.error('Failed to call multipass:', multipassError)
          this.showSocialAccountMessage(
            'Account created but sign-in failed. Please try signing in.',
            true
          )
          setTimeout(() => {
            this.incompleteSocialPanel.attr('aria-hidden', 'true')
            this.clearMainSignInPanel()
            this.checkFieldValidation()
          }, 3000)
        }
      } else {
        this.showSocialAccountMessage(
          responseData?.message || 'An error occurred. Please try again.',
          true
        )
      }
    } catch (error) {
      console.error('Failed to create account from social:', error)
      this.showSocialAccountMessage(
        'An error occurred. Please try again.',
        true
      )
    }
  }
}

const accountPanel = new AccountPanel()
var Pura = window.Pura || {}

Pura.accountPanel = accountPanel

export { accountPanel }
